import React, { useEffect, useState } from "react";
import { LogOut, Users } from "lucide-react";
import { Panel, Eyebrow } from "@/components/Panel";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { useDemo } from "@/store/DemoStore";
import { api, errText, TOKEN_KEY, EVENT_KEY } from "@/lib/devadminApi";
import { DevAdminLogin } from "@/pages/devadmin/DevAdminLogin";
import { Integrations } from "@/pages/devadmin/Integrations";
import { Meals } from "@/pages/devadmin/Meals";
import { Fees } from "@/pages/devadmin/Fees";
import { EventDetails } from "@/pages/devadmin/EventDetails";
import { CategoriesRounds } from "@/pages/devadmin/CategoriesRounds";
import { WhatsAppTemplates } from "@/pages/devadmin/WhatsAppTemplates";
import { ExpenseCategories } from "@/pages/devadmin/ExpenseCategories";
import { VoiceScripts } from "@/pages/devadmin/VoiceScripts";
import { ConclaveRules } from "@/pages/devadmin/ConclaveRules";
import { SoftLaunch } from "@/pages/devadmin/SoftLaunch";
import { OpsAlerts } from "@/pages/devadmin/OpsAlerts";
import { RateLimits } from "@/pages/devadmin/RateLimits";
import { MemberDirectory } from "@/pages/devadmin/MemberDirectory";
import { ContactVerification } from "@/pages/devadmin/ContactVerification";
import { GuestSpeakers } from "@/pages/devadmin/GuestSpeakers";
import { RegistrationsConsole } from "@/pages/admin/AdminRegistrations";

const TABS = [
  ["event", "Event Details"], ["registrations", "Registrations"], ["categories", "Categories & Rounds"],
  ["conclave", "Conclave Rules"],
  ["guests", "Guest Speakers"],
  ["integrations", "Integrations"],
  ["meals", "Meal Windows"], ["fees", "Fee Configuration"], ["expenses", "Expense Categories"],
  ["directory", "Member Directory"],
  ["voice", "Voice Scripts"],
  ["whatsapp", "WhatsApp Templates"], ["softlaunch", "Soft Launch"], ["ratelimits", "Rate Limits"], ["simulate", "Simulate As User"],
];

export default function DevAdmin() {
  const [user, setUser] = useState(undefined);
  useEffect(() => {
    if (!localStorage.getItem(TOKEN_KEY)) return setUser(null);
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => { localStorage.removeItem(TOKEN_KEY); setUser(null); });
  }, []);

  const logout = () => { localStorage.removeItem(TOKEN_KEY); setUser(null); };

  return (
    <div className="min-h-screen">
      <Toaster theme="dark" position="top-right" toastOptions={{ style: { background: "var(--panel)", border: "1px solid var(--hairline)", color: "var(--text)", borderRadius: 0 } }} />
      {user === undefined ? <div className="p-10 mono-label">Checking session…</div>
        : user === null ? <DevAdminLogin onSuccess={setUser} />
        : <Console user={user} onLogout={logout} />}
    </div>
  );
}

function Console({ user, onLogout }) {
  const [events, setEvents] = useState([]);
  const [event, setEvent] = useState(null);

  const loadEvents = async (preferId) => {
    try {
      const { data } = await api.get("/events");
      setEvents(data);
      const id = preferId || localStorage.getItem(EVENT_KEY);
      const pick = data.find((e) => e.id === id) || data[0];
      if (pick) selectEvent(pick.id);
    } catch (e) { toast.error(errText(e)); }
  };
  const selectEvent = async (id) => {
    localStorage.setItem(EVENT_KEY, id);
    try { const { data } = await api.get(`/events/${id}`); setEvent(data); } catch (e) { toast.error(errText(e)); }
  };
  useEffect(() => { loadEvents(); }, []); // eslint-disable-line

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-8 py-10">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Eyebrow>Devadmin · System Controls</Eyebrow>
          <h1 className="h-hero mt-3 text-4xl sm:text-5xl">The wiring behind the show.</h1>
        </div>
        <div className="flex items-center gap-3">
          <select data-testid="event-selector" className="input-x !w-auto !py-2" value={event?.id || ""} onChange={(e) => selectEvent(e.target.value)}>
            {events.map((e) => <option key={e.id} value={e.id}>{e.name} · {e.status}</option>)}
          </select>
          <button data-testid="devadmin-logout" onClick={onLogout} className="btn-ghost" title={`${user.name} · ${user.phone}`}><LogOut size={14} /> {user.name}</button>
        </div>
      </div>

      {!event ? <div className="mt-8 mono-label" data-testid="event-loading">Loading event…</div> : (
        <Tabs defaultValue="event" className="mt-8">
          <TabsList className="!bg-transparent !rounded-none !p-0 border-b border-[var(--hairline)] gap-0 w-full flex flex-wrap h-auto">
            {TABS.map(([v, l]) => (
              <TabsTrigger key={v} value={v} data-testid={`devadmin-tab-${v}`}
                className="!rounded-none px-5 py-3 text-sm mono-label data-[state=active]:!bg-transparent data-[state=active]:!text-[var(--text)] data-[state=active]:!border-b-2 data-[state=active]:!border-[var(--red)] text-[var(--taupe)] !border-0">
                {l}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="event" className="mt-6">
            <EventDetails event={event} onSaved={(ev) => { setEvent(ev); setEvents(events.map((e) => e.id === ev.id ? { ...e, ...ev } : e)); }} onCloned={(ev) => loadEvents(ev.id)} />
          </TabsContent>
          <TabsContent value="registrations" className="mt-6"><RegistrationsConsole canDelete embedded /></TabsContent>
          <TabsContent value="categories" className="mt-6"><CategoriesRounds eventId={event.id} /></TabsContent>
          <TabsContent value="conclave" className="mt-6"><ConclaveRules eventId={event.id} /></TabsContent>
          <TabsContent value="guests" className="mt-6"><GuestSpeakers eventId={event.id} /></TabsContent>
          <TabsContent value="integrations" className="mt-6"><Integrations eventId={event.id} /></TabsContent>
          <TabsContent value="meals" className="mt-6"><Meals eventId={event.id} /></TabsContent>
          <TabsContent value="fees" className="mt-6"><Fees eventId={event.id} /></TabsContent>
          <TabsContent value="expenses" className="mt-6"><ExpenseCategories eventId={event.id} /></TabsContent>
          <TabsContent value="directory" className="mt-6"><MemberDirectory eventId={event.id} /></TabsContent>
          <TabsContent value="voice" className="mt-6"><VoiceScripts eventId={event.id} /></TabsContent>
          <TabsContent value="whatsapp" className="mt-6"><WhatsAppTemplates eventId={event.id} /></TabsContent>
          <TabsContent value="softlaunch" className="mt-6"><SoftLaunch eventId={event.id} /><div className="mt-6"><ContactVerification eventId={event.id} /></div><OpsAlerts eventId={event.id} /></TabsContent>
          <TabsContent value="ratelimits" className="mt-6"><RateLimits /></TabsContent>
          <TabsContent value="simulate" className="mt-6"><Simulate /></TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function Simulate() {
  const nav = useNavigate();
  const { dispatch } = useDemo();
  const opts = [
    { id: "public_registration", route: "/register", label: "Public Registration", desc: "Land as a first-time visitor" },
    { id: "member_dashboard", route: "/member", label: "Member Portal", desc: "OTP-verified member view" },
    { id: "table_leader", route: "/leader", label: "Table Leader App", desc: "Round-table with drag-drop referral logging" },
    { id: "scanner_app", route: "/scanner", label: "Scanner App", desc: "Meal check-in simulator" },
    { id: "admin_dashboard", route: "/admin", label: "Admin Dashboard", desc: "Live event command centre" },
  ];
  return (
    <Panel className="p-6">
      <div className="flex items-center gap-3">
        <Users size={16} style={{ color: "var(--red-2)" }} />
        <div>
          <div className="font-serif text-xl">Simulate as User</div>
          <div className="mono-label mt-1" style={{ color: "var(--taupe)" }}>Jump into any role in one click — for demo & QA</div>
        </div>
      </div>
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-0">
        {opts.map((o, i) => (
          <button key={o.id} data-testid={`sim-${o.id}`} onClick={() => { dispatch({ type: "SET_ROLE", role: o.id }); nav(o.route); }}
            className="text-left p-5 hairline hover:border-[var(--red)] transition-colors" style={{ marginLeft: i % 2 === 1 ? -1 : 0, marginTop: i > 1 ? -1 : 0 }}>
            <div className="text-sm font-medium">{o.label}</div>
            <div className="mono-label mt-1" style={{ color: "var(--taupe)" }}>{o.desc}</div>
          </button>
        ))}
      </div>
    </Panel>
  );
}
